/**
 * One class: the drill-down step between a school and its lessons.
 *
 * The same workspace a teacher sees on their dashboard, for a single class.
 * Rules decide access: the class's teachers, and its school's admin and
 * principal.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft } from 'lucide-react';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  getClassResults,
  getClassStudents,
  getPassThreshold,
  getRecentTeacherSessions,
  type RosterStudent,
  type SessionSummaryRow,
} from '../../services/performanceService';
import { DEFAULT_PASS_THRESHOLD, type Result } from '../../lib/analytics/metrics';
import { ClassPerformanceWorkspace } from '../../Components/analytics/ClassPerformanceWorkspace';
import { AnalyticsEmptyState, AnalyticsPanel } from '../../Components/analytics/AnalyticsPanel';
import { learnXRFontStyle } from '../../Components/LearnXRTypography';
import { Skeleton } from '../../Components/ui/skeleton';

const ClassAnalyticsPage = () => {
  const { classId = '' } = useParams<{ classId: string }>();
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const [className, setClassName] = useState<string | null>(null);
  const [schoolId, setSchoolId] = useState<string | undefined>(undefined);
  const [results, setResults] = useState<Result[]>([]);
  const [students, setStudents] = useState<RosterStudent[]>([]);
  const [sessions, setSessions] = useState<SessionSummaryRow[] | null>(null);
  const [passThreshold, setPassThreshold] = useState(DEFAULT_PASS_THRESHOLD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!profile) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'classes', classId));
        if (!snap.exists()) {
          if (!cancelled) setDenied(true);
          return;
        }
        const c = snap.data();
        const schoolId: string = c.school_id;
        if (!cancelled) {
          setClassName(c.class_name || 'Class');
          setSchoolId(schoolId);
        }
        const [list, roster, threshold] = await Promise.all([
          getClassResults(classId, schoolId),
          getClassStudents([classId], schoolId).catch(() => [] as RosterStudent[]),
          getPassThreshold(schoolId),
        ]);
        if (cancelled) return;
        setResults(list);
        setStudents(roster);
        setPassThreshold(threshold);
        // A teacher can list their own sessions. The rules don't let anyone else
        // list a school's sessions, so for them the workspace counts sessions
        // from the results instead.
        if (profile.role === 'teacher' && user?.uid) {
          const sessionList = await getRecentTeacherSessions(user.uid, 300).catch(() => null);
          if (!cancelled) setSessions(sessionList);
        }
      } catch (err) {
        console.error('[ClassAnalyticsPage] load failed:', err);
        if (!cancelled) {
          if ((err as { code?: string })?.code === 'permission-denied') setDenied(true);
          else setError('This class’s results could not be loaded. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classId, profile, user?.uid]);

  return (
    <div className="min-h-screen bg-background pb-12 pt-24">
      <div className="mx-auto max-w-6xl space-y-5 px-4 sm:px-6">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        {denied ? (
          <AnalyticsPanel title="Class not available">
            <AnalyticsEmptyState title="This class doesn’t exist or isn’t yours to view" description="Teachers see their own classes; schools see theirs." />
          </AnalyticsPanel>
        ) : (
          <>
            <header>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Class analytics</p>
              {className ? (
                <h1 className="mt-1 text-2xl font-bold text-foreground" style={learnXRFontStyle}>
                  {className}
                </h1>
              ) : (
                <Skeleton className="mt-2 h-7 w-48" />
              )}
            </header>
            <ClassPerformanceWorkspace
              title="Performance"
              classes={[{ id: classId, name: className ?? 'Class' }]}
              students={students}
              results={results}
              sessions={sessions}
              passThreshold={passThreshold}
              loading={loading}
              error={error}
              schoolId={profile?.role === 'admin' || profile?.role === 'superadmin' ? schoolId : undefined}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ClassAnalyticsPage;
